import type { DieuChinh, LoaiDieuChinh } from '@qlk/shared';
import {
  DAU_GOI_Y_DIEU_CHINH,
  LOAI_DIEU_CHINH,
  LOAI_DIEU_CHINH_LABEL,
  nhanKyDoiSoat,
  ngayDayVn,
} from '@qlk/shared';
import { useState } from 'react';

import { HopThoai, Nut, O, OChonTruong, dinhDangTien } from '../../../ui/index.js';
import { useLuuDieuChinh, useXoaDieuChinh } from '../api.js';

export interface HopDieuChinhProps {
  period: string;
  userId: string;
  tenNguoi: string;
  /** Các khoản đã có của người này trong kỳ, để xem và xoá ngay trong hộp. */
  dangCo: DieuChinh[];
  onDong: () => void;
}

/**
 * Thêm / xoá điều chỉnh của một người trong một kỳ.
 *
 * Dấu tiền **do người nhập quyết định**: hộp chỉ chọn sẵn `+`/`−` theo loại
 * (xem `DAU_GOI_Y_DIEU_CHINH`) rồi cho đổi. Thu lại một khoản thưởng đã trả
 * nhầm là dòng `thuong` âm — chặn dấu theo loại sẽ buộc kế toán ghi sai loại
 * để lách.
 */
export function HopDieuChinh({ period, userId, tenNguoi, dangCo, onDong }: HopDieuChinhProps) {
  const luu = useLuuDieuChinh();
  const xoa = useXoaDieuChinh();

  const [loai, setLoai] = useState<LoaiDieuChinh>('thuong');
  const [am, setAm] = useState(false);
  const [usd, setUsd] = useState('');
  const [lyDo, setLyDo] = useState('');

  const soUsd = Math.abs(Number(usd.replace(',', '.')));
  const hopLe = Number.isFinite(soUsd) && soUsd >= 0.01 && lyDo.trim().length >= 3;
  const kySo = am ? -soUsd : soUsd;

  return (
    <HopThoai
      title={`Điều chỉnh — ${tenNguoi}`}
      rong="lg"
      onDong={onDong}
      chanHop={
        <>
          {luu.isError && (
            <p role="alert" className="mr-auto self-center text-phu text-bad-chu">
              {luu.error.message}
            </p>
          )}
          <Nut kieu="phu" onClick={onDong}>
            Đóng
          </Nut>
          <Nut
            kieu="chinh"
            disabled={!hopLe}
            dangChay={luu.isPending}
            onClick={() => {
              luu.mutate(
                { period, userId, loai, usd: kySo, lyDo: lyDo.trim() },
                {
                  onSuccess: () => {
                    setUsd('');
                    setLyDo('');
                  },
                },
              );
            }}
          >
            Thêm khoản
          </Nut>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-phu text-chu-mo">
          {nhanKyDoiSoat(period)} · khoản điều chỉnh cộng/trừ vào phần chia để ra số phải trả.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <OChonTruong
            label="Loại"
            value={loai}
            onChange={(v) => {
              const l = v as LoaiDieuChinh;
              setLoai(l);
              setAm(DAU_GOI_Y_DIEU_CHINH[l] === -1);
            }}
            options={LOAI_DIEU_CHINH.map((l) => ({ value: l, label: LOAI_DIEU_CHINH_LABEL[l] }))}
          />
          <O
            label="Số tiền (US$)"
            type="number"
            min={0}
            step={1}
            value={usd}
            onChange={setUsd}
            moTa="Nhập số dương, chọn dấu bên dưới"
          />
        </div>

        <fieldset className="flex flex-wrap gap-4">
          <legend className="mb-1 text-phu font-medium text-chu-phu">Dấu</legend>
          {[
            { giaTri: false, nhan: '+ Cộng vào phải trả' },
            { giaTri: true, nhan: '− Trừ khỏi phải trả' },
          ].map((o) => (
            <label key={String(o.giaTri)} className="flex items-center gap-2 text-co-ban text-chu">
              <input
                type="radio"
                name="dau-dieu-chinh"
                checked={am === o.giaTri}
                onChange={() => {
                  setAm(o.giaTri);
                }}
                className="h-4 w-4"
              />
              {o.nhan}
            </label>
          ))}
        </fieldset>

        <O label="Lý do" value={lyDo} onChange={setLyDo} nhieuDong required />

        {soUsd >= 0.01 && (
          <p className="rounded-[10px] bg-info-nen px-3 py-2 text-phu text-info-chu">
            Sẽ ghi {LOAI_DIEU_CHINH_LABEL[loai].toLowerCase()} <strong>{dinhDangTien(kySo)}</strong>{' '}
            cho {tenNguoi}.
          </p>
        )}

        <div>
          <h3 className="mb-2 text-co-ban font-semibold text-chu">
            Đã có trong kỳ ({dangCo.length})
          </h3>
          {dangCo.length === 0 ? (
            <p className="text-phu text-chu-mo">Chưa có khoản nào.</p>
          ) : (
            <ul className="divide-y divide-vien rounded-[10px] border border-vien">
              {dangCo.map((d) => (
                <li key={d.id} className="flex flex-wrap items-start gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-co-ban text-chu">
                      {LOAI_DIEU_CHINH_LABEL[d.loai]} ·{' '}
                      <span className="tabular-nums font-semibold">{dinhDangTien(d.usd)}</span>
                    </p>
                    <p className="text-phu text-chu-mo">{d.lyDo}</p>
                    <p className="text-phu text-chu-mo">
                      {d.nguoiTao ?? 'không rõ'} · {ngayDayVn(d.createdAt.slice(0, 10))}
                    </p>
                  </div>
                  <Nut
                    kieu="nhe"
                    dangChay={xoa.isPending}
                    onClick={() => {
                      xoa.mutate({ id: d.id });
                    }}
                  >
                    Xoá
                  </Nut>
                </li>
              ))}
            </ul>
          )}
          {xoa.isError && (
            <p role="alert" className="mt-2 text-phu text-bad-chu">
              {xoa.error.message}
            </p>
          )}
        </div>
      </div>
    </HopThoai>
  );
}
